<div class="event-panel" [formGroup]="eventForm">

  <!-- 1. HEADER BAR -->
  <div class="panel-header">
    <div class="header-title-wrapper">
      <input type="text" formControlName="title" placeholder="Event title" class="header-title-input" />
      <app-validation-error [formGroup]="eventForm" [controlName]="'title'"></app-validation-error>
    </div>
    <button class="close-btn" (click)="close()">
      <i class="fas fa-times"></i>
    </button>
  </div>

  <!-- 2. ACTION STRIP -->
  <div class="action-strip">
    <div class="action-left">
      <button class="save-btn" (click)="save()">
        {{ editEventData?.id ? 'Update' : 'Save' }}
      </button>
      <button *ngIf="editEventData?.id" class="delete-btn" [class.confirm]="confirmDelete" (click)="deleteEvent()">
        <i class="fas fa-trash-alt"></i> {{ confirmDelete ? 'Confirm?' : 'Delete' }}
      </button>
      <button *ngIf="editEventData?.id" class="duplicate-btn" (click)="duplicateEvent()" title="Duplicate">
        <i class="fas fa-copy"></i>
      </button>
      <ng-container *ngIf="referenceLink as link">
        <span class="action-divider"></span>
        <button class="goto-reference-btn" (click)="goToReference()" [title]="'Open ' + link.label + ' #' + link.number"
          [style.color]="link.color"
          [style.backgroundColor]="tint(link.color, 0.1)"
          [style.borderColor]="tint(link.color, 0.35)">
          <i class="fas" [ngClass]="link.icon"></i> {{ link.label }} #{{ link.number }}
        </button>
      </ng-container>
    </div>
    <div class="action-right">
      <button class="privacy-toggle" [class.private]="isPrivate" (click)="togglePrivate()">
        <i class="fas" [ngClass]="isPrivate ? 'fa-lock' : 'fa-lock-open'"></i>
        {{ isPrivate ? 'Private' : 'Public' }}
      </button>
      <button class="reminder-icon-btn" [class.active]="selectedReminderOption" [matMenuTriggerFor]="reminderMenu"
        [matTooltip]="selectedReminderOption || 'Set reminder'" matTooltipPosition="below">
        <i class="fas fa-bell"></i>
      </button>
      <mat-menu #reminderMenu="matMenu" class="reminder-dropdown-menu">
        <button *ngFor="let option of reminderOptions" mat-menu-item class="reminder-menu-item"
          [class.active]="selectedReminderOption === option"
          (click)="selectReminderOption(option)">
          {{ option }}
          <i class="fas fa-check" *ngIf="selectedReminderOption === option"></i>
        </button>
      </mat-menu>
    </div>
  </div>

  <!-- 3. SCROLLABLE BODY -->
  <div class="panel-body">

    <!-- 3A. CORE FIELDS -->
    <div class="core-fields">

      <!-- Calendar Group selector row -->
      <div class="field-row calendar-row" *ngIf="calendarGroups.length > 0">
        <i class="fas fa-layer-group field-icon"></i>
        <div class="calendar-select-wrapper">
          <ng-select
            [items]="calendarGroups"
            bindLabel="title"
            [(ngModel)]="selectedGroup"
            [ngModelOptions]="{ standalone: true }"
            placeholder="Select group"
            [clearable]="false"
            (change)="onGroupChange($event)"
          >
          </ng-select>
        </div>
      </div>

      <!-- Calendar pills row -->
      <div class="field-row type-row" *ngIf="calendarsInGroup.length > 0">
        <i class="fas fa-tag field-icon"></i>
        <div class="event-type-pills">
          <button *ngFor="let cal of calendarsInGroup" class="type-pill"
            [class.active]="calendarId === cal.id"
            (click)="calendarId = cal.id; getCategoriesByCalendarId()">
            <span class="calendar-color-dot" [ngStyle]="{ 'background-color': cal.color || '#94a3b8' }"></span>
            {{ cal.title }}
          </button>
        </div>
      </div>

      <!-- Categories dropdown + chips -->
      <div class="field-row categories-row">
        <i class="fas fa-tags field-icon"></i>
        <div class="categories-wrapper">
          <div class="categories-actions">
            <button class="category-menu-btn" [matMenuTriggerFor]="categoriesMenu">
              Categories <i class="fas fa-chevron-down"></i>
            </button>
          </div>
          <mat-chip-listbox *ngIf="dropdownSelectedCategories.length > 0" class="chip-list" aria-label="Selected Categories">
            <mat-chip-option *ngFor="let cat of dropdownSelectedCategories" [removable]="true" (removed)="toggleCategory(cat)">
              <i class="fas fa-tag" [ngStyle]="{ color: cat.color }"></i>
              <span class="pl-1">{{ cat.title }}</span>
              <button matChipRemove><mat-icon>cancel</mat-icon></button>
            </mat-chip-option>
          </mat-chip-listbox>
        </div>
      </div>

      <!-- Date & Time row -->
      <div class="field-row date-time-row">
        <i class="fas fa-clock field-icon"></i>
        <div class="date-time-wrapper">
          <div *ngIf="isRestrictedCalendarType()" class="restricted-calendar-warning">
            <i class="fas fa-exclamation-triangle"></i>
            <span>Date and time fields cannot be modified in this event.</span>
          </div>
          <div class="date-time-row-inner" [ngStyle]="{ 'align-items': isTimeZoneToggled ? 'baseline' : 'center' }">
            <div class="main-fields">
              <a-ng-container *ngIf="!eventForm.get('isAllDay')?.value">
                <ng-container *ngIf="!isTimeZoneToggled">
                  <div class="field-row-inner">
                    <mat-form-field appearance="outline" class="date-picker-field">
                      <input matInput [matDatepicker]="startPicker" placeholder="MM/DD/YYYY"
                        [disabled]="isRestrictedCalendarType()" formControlName="selectedDate"
                        (dateChange)="onSelectedDateChange($event.value)" />
                      <mat-datepicker-toggle matSuffix [for]="startPicker" [disabled]="isRestrictedCalendarType()"></mat-datepicker-toggle>
                      <mat-datepicker #startPicker></mat-datepicker>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="time-picker-field">
                      <input matInput [matTimepicker]="startTp" placeholder="Start" formControlName="startTime"
                        [disabled]="isRestrictedCalendarType()">
                      <mat-timepicker-toggle matIconSuffix [for]="startTp" class="time-picker-icon"></mat-timepicker-toggle>
                      <mat-timepicker #startTp interval="15 minutes" panelClass="event-time-panel" (selected)="onStartTimeChange($event)"></mat-timepicker>
                    </mat-form-field>
                    <span class="time-separator">to</span>
                    <mat-form-field appearance="outline" class="time-picker-field">
                      <input matInput [matTimepicker]="endTp" placeholder="End" formControlName="endTime"
                        [matTimepickerMin]="minEndTime" [disabled]="!isEndTimeEnabled || isRestrictedCalendarType()">
                      <mat-timepicker-toggle matIconSuffix [for]="endTp" class="time-picker-icon"></mat-timepicker-toggle>
                      <mat-timepicker #endTp interval="15 minutes" panelClass="event-time-panel"></mat-timepicker>
                    </mat-form-field>
                  </div>
                </ng-container>
                <ng-container *ngIf="isTimeZoneToggled">
                  <div class="field-row-inner">
                    <mat-form-field appearance="outline" class="date-picker-field">
                      <input matInput [matDatepicker]="startPicker" placeholder="MM/DD/YYYY"
                        [disabled]="isRestrictedCalendarType()" formControlName="selectedDate"
                        (dateChange)="onSelectedDateChange($event.value)" />
                      <mat-datepicker-toggle matSuffix [for]="startPicker" [disabled]="isRestrictedCalendarType()"></mat-datepicker-toggle>
                      <mat-datepicker #startPicker></mat-datepicker>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="time-picker-field">
                      <input matInput [matTimepicker]="tzStartTp" placeholder="Start" formControlName="startTime"
                        [disabled]="isRestrictedCalendarType()">
                      <mat-timepicker-toggle matIconSuffix [for]="tzStartTp" class="time-picker-icon"></mat-timepicker-toggle>
                      <mat-timepicker #tzStartTp interval="15 minutes" panelClass="event-time-panel" (selected)="onStartTimeChange($event)"></mat-timepicker>
                    </mat-form-field>
                    <ng-select [closeOnSelect]="true" bindLabel="text" formControlName="startTimeZone"
                      [items]="starttimeZones" placeholder="Timezone" [disabled]="isRestrictedCalendarType()"
                      (change)="onStartTimeZoneChange($event)">
                    </ng-select>
                  </div>
                  <div class="field-row-inner">
                    <mat-form-field appearance="outline" class="date-picker-field">
                      <input matInput [matDatepicker]="endPicker" placeholder="MM/DD/YYYY"
                        [disabled]="isRestrictedCalendarType()" formControlName="selectedEndDate" />
                      <mat-datepicker-toggle matSuffix [for]="endPicker" [disabled]="isRestrictedCalendarType()"></mat-datepicker-toggle>
                      <mat-datepicker #endPicker></mat-datepicker>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="time-picker-field">
                      <input matInput [matTimepicker]="tzEndTp" placeholder="End" formControlName="endTime"
                        [matTimepickerMin]="minEndTime" [disabled]="!isEndTimeEnabled || isRestrictedCalendarType()">
                      <mat-timepicker-toggle matIconSuffix [for]="tzEndTp" class="time-picker-icon"></mat-timepicker-toggle>
                      <mat-timepicker #tzEndTp interval="15 minutes" panelClass="event-time-panel"></mat-timepicker>
                    </mat-form-field>
                    <ng-select [closeOnSelect]="true" bindLabel="text" [items]="endtimeZones"
                      placeholder="Timezone" [disabled]="true"
                      [ngModel]="eventForm.get('startTimeZone')?.value"
                      [ngModelOptions]="{ standalone: true }">
                    </ng-select>
                  </div>
                </ng-container>
              </a-ng-container>

              <a-ng-container *ngIf="eventForm.get('isAllDay')?.value">
                <div class="field-row-inner">
                  <mat-form-field appearance="outline" class="date-picker-field">
                    <input matInput [matDatepicker]="picker" placeholder="MM/DD/YYYY"
                      formControlName="isAllselectedstartDate"
                      (dateChange)="onStartDateChange($event.value)"
                      [disabled]="isRestrictedCalendarType()" />
                    <mat-datepicker-toggle matSuffix [for]="picker" [disabled]="isRestrictedCalendarType()"></mat-datepicker-toggle>
                    <mat-datepicker #picker></mat-datepicker>
                  </mat-form-field>
                  <div class="days-picker-wrapper">
                    <div [matMenuTriggerFor]="daysMenu"
                      class="d-flex justify-content-between align-items-center px-2 cursor-pointer"
                      [class.disabled]="isRestrictedCalendarType()">
                      <span>{{ eventForm.get('selectedDayOption')?.value.label || '1 day' }}</span>
                      <i class="fas fa-chevron-down" *ngIf="!isRestrictedCalendarType()"></i>
                    </div>
                  </div>
                </div>
                <div class="field-row-inner" *ngIf="showEndDatePicker">
                  <mat-form-field appearance="outline" class="date-picker-field">
                    <input matInput [matDatepicker]="picker2" placeholder="MM/DD/YYYY"
                      [formControl]="isAllselectedEndDateControl"
                      (dateChange)="onEndDateChange($event.value)"
                      [disabled]="isRestrictedCalendarType()"
                      [readonly]="eventForm.get('selectedDayOption')?.value.label !== 'custom' || isRestrictedCalendarType()" />
                    <mat-datepicker-toggle matSuffix [for]="picker2" [disabled]="isRestrictedCalendarType()"></mat-datepicker-toggle>
                    <mat-datepicker #picker2></mat-datepicker>
                  </mat-form-field>
                </div>
              </a-ng-container>
            </div>
            <div class="date-time-controls">
              <span *ngIf="!eventForm.get('isAllDay')?.value" (click)="toggleTimeZone()"
                matTooltip="Time zone" matTooltipPosition="above" class="tz-toggle"
                [class.disabled]="isRestrictedCalendarType()">
                <i class="fas fa-globe"></i>
              </span>
              <button matTooltip="Add additional slot" mat-mini-fab class="plus-fab-button" (click)="addAdditionalSlot()">
                <mat-icon>add</mat-icon>
              </button>
              <mat-slide-toggle *ngIf="!isTimeZoneToggled" class="b-one-switch" formControlName="isAllDay"
                (change)="onAllDayToggle()" [disabled]="isRestrictedCalendarType()">All day</mat-slide-toggle>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional time slots -->
      <div class="field-row additional-slots-row-outer" *ngIf="additionalSlots.length > 0">
        <i class="fas fa-clock field-icon"></i>
        <div class="additional-date-time-slots-wrapper" formArrayName="additionalSlots">
          <div class="additional-slots-row" *ngFor="let grp of additionalSlots.controls; let i = index" [formGroupName]="i">
            <mat-form-field appearance="outline" class="additional-date-picker-field">
              <input matInput [matDatepicker]="additionalStartPicker" placeholder="MM/DD/YYYY"
                formControlName="startDate" (dateChange)="onAdditionalStartDateChange(i, $event.value)" />
              <mat-datepicker-toggle matSuffix [for]="additionalStartPicker"></mat-datepicker-toggle>
              <mat-datepicker [startAt]="grp?.get('startDate')?.value" #additionalStartPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="time-picker-field">
              <input matInput [matTimepicker]="addStartTp" placeholder="Start" formControlName="startTime">
              <mat-timepicker-toggle matIconSuffix [for]="addStartTp" class="time-picker-icon"></mat-timepicker-toggle>
              <mat-timepicker #addStartTp interval="15 minutes" panelClass="event-time-panel"></mat-timepicker>
            </mat-form-field>
            <span class="time-separator">To</span>
            <mat-form-field appearance="outline" class="additional-date-picker-field">
              <input matInput [matDatepicker]="additionalEndPicker" placeholder="MM/DD/YYYY"
                formControlName="endDate" [min]="grp?.get('startDate')?.value"
                (dateChange)="onAdditionalEndDateChange(i, $event.value)" />
              <mat-datepicker-toggle matSuffix [for]="additionalEndPicker"></mat-datepicker-toggle>
              <mat-datepicker [startAt]="grp?.get('endDate')?.value" #additionalEndPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="time-picker-field">
              <input matInput [matTimepicker]="addEndTp" placeholder="End" formControlName="endTime">
              <mat-timepicker-toggle matIconSuffix [for]="addEndTp" class="time-picker-icon"></mat-timepicker-toggle>
              <mat-timepicker #addEndTp interval="15 minutes" panelClass="event-time-panel"></mat-timepicker>
            </mat-form-field>
            <button mat-icon-button class="delete-slot-btn" matTooltip="Delete slot" (click)="removeAdditionalSlot(i)">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Recurrence placeholder -->
      <!-- <div class="field-row recurrence-row">
        <i class="fas fa-redo field-icon"></i>
        <select class="recurrence-select" disabled>
          <option>Does not repeat</option>
        </select>
      </div> -->
    </div>

    <!-- 3B. EXPANDABLE SECTIONS -->

    <!-- Location & Attendees -->
    <div class="expandable-section">
      <div class="section-header" (click)="toggleSection('locationAttendees')">
        <span class="section-label"><i class="fas fa-map-marker-alt"></i> Location & Attendees</span>
        <span class="indicators">
          <span class="content-dot" *ngIf="sectionHasContent('locationAttendees') && !expandedSections['locationAttendees']"></span>
          <i class="fas" [ngClass]="expandedSections['locationAttendees'] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        </span>
      </div>
      <div class="section-body" *ngIf="expandedSections['locationAttendees']">
        <!-- Attendees -->
        <div class="section-field">
          <ng-container *ngIf="selectedAttendeesFromDropdown.length > 0">
            <mat-chip-listbox class="chip-list" aria-label="Selected attendees">
              <mat-chip-option *ngFor="let attendee of selectedAttendeesFromDropdown" [removable]="true" (removed)="removeAttendee(attendee)">
                <app-profile-avatar [fullName]="attendee.name" [widthSize]="'25px'" [heightSize]="'25px'"
                  [userStatusSlug]="attendee?.user_status?.value || 'available'"
                  [profileUrl]="attendee?.user_detail?.profile_image?.url"></app-profile-avatar>
                {{ attendee.name }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-option>
            </mat-chip-listbox>
          </ng-container>
          <input class="section-input" type="text" placeholder="Invite attendees"
            (keydown.enter)="getInviteAttendeesEnter($event)" formControlName="attendees"
            [matMenuTriggerFor]="inviteAttendees.length > 0 ? inviteAttendeesMenu : null"
            #inviteAttendeesMenuTrigger="matMenuTrigger" />
        </div>
        <!-- Location -->
        <div class="section-field">
          <ng-container *ngIf="selectedLocationsFromDropdown.length > 0">
            <mat-chip-listbox class="chip-list" aria-label="Selected Locations">
              <mat-chip-option *ngFor="let location of selectedLocationsFromDropdown" [removable]="true" (removed)="removeLocation(location)">
                {{ location.address }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-option>
            </mat-chip-listbox>
          </ng-container>
          <input class="section-input" type="text" placeholder="Search for a location"
            (keydown.enter)="getLocationEnter($event)" formControlName="locations"
            [matMenuTriggerFor]="locationMenu" #locationMenuTrigger="matMenuTrigger" />
        </div>
      </div>
    </div>

    <!-- Description -->
    <div class="expandable-section">
      <div class="section-header" (click)="toggleSection('description')">
        <span class="section-label"><i class="fas fa-align-left"></i> Description</span>
        <span class="indicators">
          <span class="content-dot" *ngIf="sectionHasContent('description') && !expandedSections['description']"></span>
          <i class="fas" [ngClass]="expandedSections['description'] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        </span>
      </div>
      <div class="section-body" *ngIf="expandedSections['description']">
        <ckeditor [config]="ckeConfig" [editor]="Editor" formControlName="description"
          class="event-editor description-editor">
        </ckeditor>
      </div>
    </div>


    <!-- Attachments -->
    <div class="expandable-section">
      <div class="section-header" (click)="toggleSection('attachments')">
        <span class="section-label"><i class="fas fa-paperclip"></i> Attachments</span>
        <span class="indicators">
          <span class="content-dot" *ngIf="sectionHasContent('attachments') && !expandedSections['attachments']"></span>
          <i class="fas" [ngClass]="expandedSections['attachments'] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        </span>
      </div>
      <div class="section-body" *ngIf="expandedSections['attachments']">
        <app-attachment
          [dropZoneId]="'addEventAttachment'"
          [fileTypes]="allowedFileTypes"
          [multipleUpload]="true"
          [multipleUploadData]="attachmentsArr"
          [multipleImagesOnEdit]="attachmentsArr"
          (callback)="attachmentId($event)"
          [module_id]="22"
          [showAllowedFileExtensions]="false"
        ></app-attachment>
      </div>
    </div>

    <!-- Bid checkbox -->
    <div *ngIf="editEventData?.slug === 'bids'" class="bid-checkbox-section">
      <mat-checkbox class="bidone-checkbox" formControlName="isUpdateBidDate">Update bid due date</mat-checkbox>
    </div>

    <div class="scroll-padding"></div>
  </div>

  <!-- 4. MOBILE BOTTOM BAR -->
  <div class="mobile-bottom-bar">
    <button (click)="save()">{{ editEventData?.id ? 'Update Event' : 'Save Event' }}</button>
  </div>
</div>

<!-- Mat menu for invite attendees -->
<mat-menu #inviteAttendeesMenu="matMenu" class="invite-attendees-menu">
  <div class="invite-attendees-list-container">
    <div *ngFor="let item of inviteAttendees"
      class="invite-attendees-item d-flex align-items-center py-2 px-3"
      (click)="onInviteAttendeesSelected(item)">
      <app-profile-avatar [fullName]="item.name" [widthSize]="'40px'" [heightSize]="'40px'"
        [userStatusSlug]="item?.user_status?.value || 'available'"
        [profileUrl]="item?.user_detail?.profile_image?.url"></app-profile-avatar>
      <div class="ml-2">
        <div class="invite-attendees-name">{{ item.name }}</div>
      </div>
    </div>
  </div>
</mat-menu>

<!-- Mat menu for location -->
<mat-menu #locationMenu="matMenu" class="location-menu">
  <div class="location-list-container">
    <div class="location-list-header">
      Use this location:
      <span (click)="onLocationSpanClick()" class="location-span-name">
        {{ eventForm.get('locations')?.value }}
      </span>
    </div>
    <div *ngFor="let item of locationList"
      class="location-item d-flex align-items-center py-2 px-3"
      (click)="onLocationSelected(item)">
      <div class="location-name">{{ item.address }}</div>
    </div>
  </div>
</mat-menu>

<!-- Mat menu for categories -->
<mat-menu #categoriesMenu="matMenu" class="categories-menu">
  <ng-container *ngFor="let cat of categoriesByCalendarId">
    <button mat-menu-item (click)="toggleCategory(cat); $event.stopPropagation()"
      [ngStyle]="isCategorySelected(cat) ? { border: '1px solid gray', 'border-radius': '5px', 'background-color': '#F5F5F5' } : {}">
      <span class="cat-icon" [ngStyle]="{ color: cat.color }"><i class="fas fa-tag"></i></span>
      <span>{{ cat.title }}</span>
      <span *ngIf="isCategorySelected(cat)" style="margin-left: auto; color: #28304e">
        <i class="fas fa-check"></i>
      </span>
    </button>
  </ng-container>
  <hr class="categories-divider" />
  <button mat-menu-item (click)="openCategoriesDialog()"><span>New category</span></button>
  <hr class="categories-divider" />
  <button mat-menu-item (click)="openManageCategoriesDialog()"><span>Manage categories</span></button>
</mat-menu>

<!-- Mat menu for days -->
<mat-menu #daysMenu="matMenu" class="days-menu">
  <ng-container *ngFor="let option of dayOptions">
    <button mat-menu-item (click)="selectDayOption(option)"
      [ngStyle]="eventForm.get('selectedDayOption')?.value === option ? { 'background-color': '#FFEFE7' } : {}">
      <span>{{ option.label }}</span>
    </button>
  </ng-container>
</mat-menu>
